import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, ArrowRight, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE_URL, getApiEndpoint } from '../lib/api';

// Image paths (files in public folder are served from root)
// Using process.env.PUBLIC_URL ensures correct path in both dev and production
const massageChairImage = `${process.env.PUBLIC_URL || ''}/images/products/massage-chair.jpg`;
const legMassagerImage = `${process.env.PUBLIC_URL || ''}/images/products/leg-massager.jpg`;
const massageStrapImage = `${process.env.PUBLIC_URL || ''}/images/products/massage-strap.jpg`;
const logoImage = `${process.env.PUBLIC_URL || ''}/images/icons/logo.png`;

const LandingPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    city: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.fullName || !formData.phone || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Phone validation
    const phoneRegex = /^[0-9]{10,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s-]/g, ''))) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Email validation (only if email is provided)
    if (formData.email && formData.email.trim() !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(getApiEndpoint('demo-requests'), formData);
      
      if (response.status === 201) {
        toast.success('Demo request submitted successfully! We\'ll contact you soon.');
        // Reset form
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          city: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting demo request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('demo-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--bg-primary))]">
      {/* Header/Navigation */}
      <header className="sticky top-0 z-50 bg-[rgb(var(--bg-primary))] border-b border-[rgb(var(--border-light))]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between py-5">
            <div className="flex items-center gap-3">
              <img 
                src={logoImage} 
                alt="BlissMe Logo" 
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.logo-fallback');
                  if (fallback) fallback.classList.remove('hidden');
                }}
              />
              <div className="w-10 h-10 rounded-full bg-[rgb(var(--brand-purple))] flex items-center justify-center logo-fallback hidden">
                <span className="text-white font-bold text-lg">BM</span>
              </div>
              <span className="text-xl font-medium text-[rgb(var(--text-primary))]">BlissMe</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#experience" className="text-[rgb(var(--text-primary))] hover:text-[rgb(var(--brand-purple))] text-sm font-medium">Experience</a>
              <a href="#products" className="text-[rgb(var(--text-primary))] hover:text-[rgb(var(--brand-purple))] text-sm font-medium">Products</a>
              <a href="#process" className="text-[rgb(var(--text-primary))] hover:text-[rgb(var(--brand-purple))] text-sm font-medium">Process</a>
              <Button 
                onClick={scrollToForm}
                className="bg-[rgb(var(--brand-purple))] hover:bg-[rgb(var(--brand-purple-dark))] text-white rounded-[12px] h-12 px-6"
              >
                Request Demo
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        {/* Purple gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-purple-50/30"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[rgb(var(--brand-purple))]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[rgb(var(--brand-purple))]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 bg-gradient-to-r from-[rgb(var(--brand-purple))] via-[rgb(var(--brand-purple-light))] to-[rgb(var(--brand-purple))] bg-clip-text text-transparent tracking-tight leading-[1.1] drop-shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-700">
              Experience Relief Before You Decide
            </h1>
            <p className="body-large text-[rgb(var(--text-secondary))] mb-12 max-w-2xl mx-auto">
              Try our massage and recovery solutions in person. No pressure, no commitment — just pure relaxation and the chance to discover what works for you.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                onClick={scrollToForm}
                className="bg-[rgb(var(--brand-purple))] hover:bg-[rgb(var(--brand-purple-dark))] text-white rounded-[12px] h-14 px-8 text-base font-medium w-full sm:w-auto shadow-lg shadow-purple-200"
              >
                Book a Free Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={scrollToForm}
                variant="outline"
                className="border-[rgb(var(--brand-purple))] text-[rgb(var(--brand-purple))] hover:bg-[rgb(var(--brand-purple))] hover:text-white rounded-[12px] h-14 px-8 text-base font-medium w-full sm:w-auto"
              >
                Leave Your Details
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Experience Section */}
      <section id="experience" className="relative py-20 bg-gradient-to-b from-purple-50/50 to-white overflow-hidden">
        <div className="absolute top-20 left-0 w-[400px] h-[400px] bg-[rgb(var(--brand-purple))]/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-1 text-center mb-4">
              What You'll Experience in the Demo
            </h2>
            <p className="body-regular text-[rgb(var(--text-secondary))] text-center mb-12">
              Our demos are designed to help you understand how our products can transform your daily wellness routine.
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="h-6 w-6 text-[rgb(var(--brand-purple))]" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Hands-On Product Usage</h3>
                  <p className="body-small text-[rgb(var(--text-secondary))]">
                    Try each product yourself and feel the difference immediately.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="h-6 w-6 text-[rgb(var(--brand-purple))]" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Guided Expert Demo</h3>
                  <p className="body-small text-[rgb(var(--text-secondary))]">
                    Our team will show you how to get the most benefit from each device.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="h-6 w-6 text-[rgb(var(--brand-purple))]" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">Pain & Relaxation Insights</h3>
                  <p className="body-small text-[rgb(var(--text-secondary))]">
                    Learn how targeted massage helps with back pain, stiffness, and stress.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle2 className="h-6 w-6 text-[rgb(var(--brand-purple))]" />
                </div>
                <div>
                  <h3 className="heading-3 mb-2">No Pressure to Buy</h3>
                  <p className="body-small text-[rgb(var(--text-secondary))]">
                    Experience first, decide later. We believe in the power of trying.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Teaser Section */}
      <section id="products" className="py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="heading-1 mb-4">Discover Our Wellness Range</h2>
            <p className="body-regular text-[rgb(var(--text-secondary))] max-w-2xl mx-auto">
              Premium massage and recovery solutions designed for everyday comfort and pain relief.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Massage Chair */}
            <Card className="hover-lift border-[rgb(var(--border-light))] rounded-[12px] overflow-hidden bg-white">
              <div className="aspect-square overflow-hidden">
                <img 
                  src={massageChairImage} 
                  alt="Luxury Massage Chair"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="heading-3 mb-3">Massage Chair</h3>
                <p className="body-small text-[rgb(var(--text-secondary))]">
                  Full-body relaxation after long days at work. Perfect for deep tissue relief and stress reduction.
                </p>
              </CardContent>
            </Card>

            {/* Leg Massager */}
            <Card className="hover-lift border-[rgb(var(--border-light))] rounded-[12px] overflow-hidden bg-white">
              <div className="aspect-square overflow-hidden">
                <img 
                  src={legMassagerImage} 
                  alt="Professional Leg Massager"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="heading-3 mb-3">Leg Massager</h3>
                <p className="body-small text-[rgb(var(--text-secondary))]">
                  Relief for tired legs and improved circulation. Ideal for professionals who stand or sit all day.
                </p>
              </CardContent>
            </Card>

            {/* Massage Strap */}
            <Card className="hover-lift border-[rgb(var(--border-light))] rounded-[12px] overflow-hidden bg-white">
              <div className="aspect-square overflow-hidden">
                <img 
                  src={massageStrapImage} 
                  alt="Handheld Massage Device"
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="heading-3 mb-3">Massage Strap</h3>
                <p className="body-small text-[rgb(var(--text-secondary))]">
                  Targeted relief for neck and shoulders. Portable and perfect for quick tension release.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How Demo Process Works */}
      <section id="process" className="relative py-20 bg-gradient-to-b from-white to-purple-50/50 overflow-hidden">
        <div className="absolute bottom-20 right-0 w-[500px] h-[500px] bg-[rgb(var(--brand-purple))]/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <h2 className="heading-1 mb-4">How the Demo Process Works</h2>
            <p className="body-regular text-[rgb(var(--text-secondary))] max-w-2xl mx-auto">
              Simple, convenient, and designed around your schedule.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[rgb(var(--brand-purple))] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-purple-200">
                1
              </div>
              <h3 className="heading-3 mb-3">Leave Your Details</h3>
              <p className="body-small text-[rgb(var(--text-secondary))]">
                Fill out the simple form below with your contact information and preferred location.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[rgb(var(--brand-purple))] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-purple-200">
                2
              </div>
              <h3 className="heading-3 mb-3">Our Team Contacts You</h3>
              <p className="body-small text-[rgb(var(--text-secondary))]">
                We'll reach out via phone within 24 hours to schedule a convenient demo time.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[rgb(var(--brand-purple))] text-white flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg shadow-purple-200">
                3
              </div>
              <h3 className="heading-3 mb-3">Experience the Demo</h3>
              <p className="body-small text-[rgb(var(--text-secondary))]">
                Enjoy a hands-on demo at your location or our nearby wellness center.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact / Demo Request Form */}
      <section id="demo-form" className="relative py-20 bg-gradient-to-br from-purple-50 via-white to-purple-50/30 overflow-hidden">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-[rgb(var(--brand-purple))]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-[rgb(var(--brand-purple))]/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="heading-1 mb-4">Request Your Free Demo</h2>
              <p className="body-regular text-[rgb(var(--text-secondary))]">
                Share your details and we'll coordinate a personalized demo experience.
              </p>
            </div>
            <Card className="border-[rgb(var(--border-light))] rounded-[12px] p-8 bg-white shadow-xl shadow-purple-100/50">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="fullName" className="text-[rgb(var(--text-primary))] mb-2 block">
                    Full Name <span className="text-[rgb(var(--brand-purple))]">*</span>
                  </Label>
                  <Input 
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className="rounded-[12px] border-[rgb(var(--border-medium))] h-12"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[rgb(var(--text-primary))] mb-2 block">
                    Phone Number <span className="text-[rgb(var(--brand-purple))]">*</span>
                    <span className="text-xs text-[rgb(var(--text-secondary))] ml-2">(Primary contact)</span>
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[rgb(var(--text-light))]" />
                    <Input 
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className="rounded-[12px] border-[rgb(var(--border-medium))] h-12 pl-11"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-[rgb(var(--text-primary))] mb-2 block">
                    Email Address <span className="text-xs text-[rgb(var(--text-secondary))] ml-2">(Optional)</span>
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[rgb(var(--text-light))]" />
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email address (optional)"
                      className="rounded-[12px] border-[rgb(var(--border-medium))] h-12 pl-11"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="city" className="text-[rgb(var(--text-primary))] mb-2 block">
                    City / Area <span className="text-[rgb(var(--brand-purple))]">*</span>
                  </Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[rgb(var(--text-light))]" />
                    <Input 
                      id="city"
                      name="city"
                      type="text"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Enter your city or area"
                      className="rounded-[12px] border-[rgb(var(--border-medium))] h-12 pl-11"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-[rgb(var(--text-primary))] mb-2 block">
                    Additional Message (Optional)
                  </Label>
                  <Textarea 
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Any specific needs or preferred demo time?"
                    className="rounded-[12px] border-[rgb(var(--border-medium))] min-h-[100px]"
                    rows={4}
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[rgb(var(--brand-purple))] hover:bg-[rgb(var(--brand-purple-dark))] text-white rounded-[12px] h-14 text-base font-medium shadow-lg shadow-purple-200"
                >
                  {isSubmitting ? 'Submitting...' : 'Request Demo'}
                </Button>

                <p className="text-xs text-[rgb(var(--text-light))] text-center">
                  No spam. No obligation. We'll contact you only for demo coordination.
                </p>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust & Reassurance Section */}
      <section className="relative py-20 bg-gradient-to-b from-purple-50/50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[rgb(var(--brand-purple))]/5 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              <div className="text-center md:text-left">
                <h3 className="heading-2 mb-4">Trusted by Real Users</h3>
                <p className="body-regular text-[rgb(var(--text-secondary))] mb-6">
                  Our massage and recovery products have been demoed and loved by urban professionals seeking relief from daily stress and pain.
                </p>
                <div className="flex items-center gap-2 justify-center md:justify-start">
                  <CheckCircle2 className="h-5 w-5 text-[rgb(var(--brand-purple))]" />
                  <span className="body-small">Demoed by 500+ users</span>
                </div>
              </div>
              <div className="text-center md:text-left">
                <h3 className="heading-2 mb-4">Why Choose BlissMe</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[rgb(var(--brand-purple))] flex-shrink-0 mt-0.5" />
                    <span className="body-small text-[rgb(var(--text-secondary))]">Designed for everyday comfort and wellness routines</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[rgb(var(--brand-purple))] flex-shrink-0 mt-0.5" />
                    <span className="body-small text-[rgb(var(--text-secondary))]">Ideal for home and office wellness</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[rgb(var(--brand-purple))] flex-shrink-0 mt-0.5" />
                    <span className="body-small text-[rgb(var(--text-secondary))]">No purchase required during demo</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-purple-100 to-purple-50 overflow-hidden">
        <div className="absolute inset-0 bg-[rgb(var(--brand-purple))]/5"></div>
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[rgb(var(--brand-purple))]/10 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-1 mb-6">
              Experience First. Decide Later.
            </h2>
            <p className="body-large text-[rgb(var(--text-secondary))] mb-8">
              Don't just read about it — feel the difference yourself. Book your free, no-obligation demo today.
            </p>
            <Button 
              onClick={scrollToForm}
              className="bg-[rgb(var(--brand-purple))] hover:bg-[rgb(var(--brand-purple-dark))] text-white rounded-[12px] h-14 px-12 text-base font-medium shadow-lg shadow-purple-300"
            >
              Experience the Demo
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[rgb(var(--border-light))] py-12 bg-gradient-to-b from-purple-50/30 to-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img 
                src={logoImage} 
                alt="BlissMe Logo" 
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  e.target.style.display = 'none';
                  const fallback = e.target.parentElement.querySelector('.logo-fallback-footer');
                  if (fallback) fallback.classList.remove('hidden');
                }}
              />
              <div className="w-10 h-10 rounded-full bg-[rgb(var(--brand-purple))] flex items-center justify-center logo-fallback-footer hidden">
                <span className="text-white font-bold text-lg">BM</span>
              </div>
              <span className="text-lg font-medium text-[rgb(var(--text-primary))]">BlissMe</span>
            </div>
            <p className="body-small text-[rgb(var(--text-secondary))] text-center">
              © 2026 BlissMe. All rights reserved. Premium massage and recovery solutions.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;